import { useField } from "formik";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Option {
  value: string;
  label: string;
}

interface SelectFieldProps {
  name: string;
  label: string;
  options: Option[];
  placeholder?: string;
  labelClassName?: string;
}

/**
 * Formik-bound shadcn Select. Mirrors `components/common/form-field.tsx` for
 * <select>-style inputs: wires value/onChange to Formik and renders the field
 * error consistently. (Radix Select forbids an empty-string item value, so
 * callers that need a "none" choice must use a non-empty sentinel.)
 */
export function SelectField({
  name,
  label,
  options,
  placeholder,
  labelClassName,
}: SelectFieldProps) {
  const [field, meta, helpers] = useField(name);
  const hasError = meta.touched && !!meta.error;

  return (
    <div className="grid gap-2">
      <Label htmlFor={name} className={labelClassName}>
        {label}
      </Label>
      <Select
        value={field.value}
        onValueChange={(value) => helpers.setValue(value)}
      >
        <SelectTrigger id={name} aria-invalid={hasError} className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}
